/**
 * secondary-header.js
 * Scroll-to-section + active tracking for the secondary header
 */
class SecondaryHeader {
  constructor(options = {}) {
    this.header = document.getElementById('secondary-header');
    this.links = Array.from(document.querySelectorAll('.secondary-header__link'));
    this.activeLink = null;
    this.sections = new Map();
    this.observer = null;
    this.debug = !!options.debug;
    this.init();
  }

  init() {
    if (!this.header || this.links.length === 0) {
      if (this.debug) console.warn('SecondaryHeader: header or links not found');
      return;
    }

    this.buildSectionsMap();
    this.bindEvents();
    this.initObserver();
    this.initInitialActivation();
    this.initHashSync();
    setTimeout(() => this.buildSectionsMap(), 300);
    setTimeout(() => this.buildSectionsMap(), 1000);

    if (this.debug) {
      console.log('SecondaryHeader initialized', {
        links: this.links.length,
        sections: this.sections.size
      });
    }
  }

  normalizeId(id) {
    if (!id) return '';
    return String(id).trim().replace(/^#/, '');
  }

  getLinkCollectionKey(link) {
    return link?.getAttribute('data-collection-key')?.trim() || '';
  }

  getLinkTargetId(link) {
    if (!link) return '';

    const dataSection = this.normalizeId(link.getAttribute('data-section'));
    if (dataSection) return dataSection;

    const href = link.getAttribute('href') || '';
    const hashIndex = href.indexOf('#');
    if (hashIndex === -1) return '';

    return this.normalizeId(href.slice(hashIndex + 1));
  }

  getLinkDesktopLabel(link) {
    const desktopText = link?.querySelector('.secondary-header__text-desktop');
    if (desktopText) return desktopText.textContent.trim();
    return link?.textContent?.trim() || '';
  }

  findLinkByCollectionKey(collectionKey) {
    return this.links.find((link) => this.getLinkCollectionKey(link) === collectionKey) || null;
  }

  findLinkByTargetId(targetId) {
    const normalizedId = this.normalizeId(targetId);
    return this.links.find((link) => this.getLinkTargetId(link) === normalizedId) || null;
  }

  findSectionElement(sectionId) {
    const id = this.normalizeId(sectionId);
    if (!id) return null;

    let el = document.getElementById(id);
    if (el) return el;

    try {
      el = document.querySelector(`[id*="${id}"]`);
      if (el) return el;
    } catch (error) {
      // Ignore invalid selectors.
    }

    el = document.querySelector(`[data-section="${sectionId}"], [data-section="${id}"]`);
    if (el) return el;

    el = document.querySelector(`.${id}`);
    if (el) return el;

    const variants = [
      id.replace(/_/g, '-'),
      id.replace(/_/g, ' '),
      id.replace(/-/g, '_'),
      id.replace(/-/g, ' '),
      id.replace(/\s+/g, '_'),
      id.replace(/\s+/g, '-')
    ];

    for (const variant of variants) {
      const variantEl =
        document.getElementById(variant) ||
        document.querySelector(`[id*="${variant}"]`) ||
        document.querySelector(`.${variant}`);

      if (variantEl) return variantEl;
    }

    return null;
  }

  buildSectionsMap() {
    this.sections.clear();

    this.links.forEach((link) => {
      const rawDataSection = this.normalizeId(link.getAttribute('data-section'));
      if (!rawDataSection) return;

      const el = this.findSectionElement(rawDataSection);
      if (el) {
        this.sections.set(rawDataSection, el);
      } else if (this.debug) {
        console.warn(`SecondaryHeader: no target found for data-section="${rawDataSection}"`);
      }
    });
  }

  bindEvents() {
    this.links.forEach((link) => {
      if (link.tagName === 'BUTTON' && !link.getAttribute('type')) {
        link.setAttribute('type', 'button');
      }

      link.addEventListener('click', (event) => {
        const rawDataSection = this.normalizeId(link.getAttribute('data-section'));
        if (!rawDataSection) {
          return;
        }

        event.preventDefault();
        window.ActiveWinningCollectionHeroId = rawDataSection;
        this.activateWinningCollectionForId(rawDataSection);

        const target = this.sections.get(rawDataSection) || this.findSectionElement(rawDataSection);
        if (!target) {
          if (this.debug) console.warn(`SecondaryHeader: Section "${rawDataSection}" not found.`);
          return;
        }

        this.setActiveLink(link);
        this.scrollToElement(target);
      });
    });
  }

  getHeaderHeight() {
    const headerComponent = document.getElementById('header-component');
    return headerComponent ? headerComponent.offsetHeight : 60;
  }

  scrollToElement(target) {
    if (!target) return;

    const headerHeight = this.getHeaderHeight();
    const secondaryHeaderHeight = this.header ? this.header.offsetHeight : 60;
    const offset = headerHeight + secondaryHeaderHeight + 20;
    const targetPosition = window.scrollY + target.getBoundingClientRect().top - offset;

    if (this.debug) console.log('Scrolling to', { target, offset, targetPosition });

    window.scrollTo({
      top: Math.max(0, Math.round(targetPosition)),
      behavior: 'smooth'
    });
  }

  scrollActiveLinkIntoView(link, behavior = 'smooth') {
    if (!link || !this.header) return;

    const nav = this.header.querySelector('.secondary-header__nav');
    if (!nav) return;

    const canScrollHorizontally = nav.scrollWidth > nav.clientWidth + 4;
    if (!canScrollHorizontally) return;

    const navRect = nav.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    const isFullyVisible =
      linkRect.left >= navRect.left + 8 &&
      linkRect.right <= navRect.right - 8;

    if (isFullyVisible) return;

    try {
      link.scrollIntoView({
        behavior,
        block: 'nearest',
        inline: 'center'
      });
    } catch (error) {
      const targetScrollLeft =
        nav.scrollLeft +
        (linkRect.left - navRect.left) -
        (nav.clientWidth / 2) +
        (linkRect.width / 2);

      nav.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior
      });
    }
  }

  setActiveLink(link) {
    if (!link || this.activeLink === link) return;

    this.links.forEach((currentLink) => {
      currentLink.classList.remove('active');
      currentLink.removeAttribute('aria-current');
    });

    link.classList.add('active');
    link.setAttribute('aria-current', 'true');
    this.activeLink = link;
    requestAnimationFrame(() => this.scrollActiveLinkIntoView(link));

    try {
      document.dispatchEvent(
        new CustomEvent('secondary-header:active-updated', {
          detail: {
            id: this.getLinkTargetId(link),
            collectionKey: this.getLinkCollectionKey(link)
          }
        })
      );
    } catch (error) {
      // Ignore custom-event errors.
    }
  }

  initObserver() {
    const elements = Array.from(this.sections.values());
    if (elements.length === 0) {
      if (this.debug) console.warn('SecondaryHeader: no sections to observe');
      return;
    }

    const headerOffset = this.getHeaderHeight() + (this.header ? this.header.offsetHeight : 60);
    const rootMargin = `-${headerOffset}px 0px -40% 0px`;
    const options = { root: null, rootMargin, threshold: 0.1 };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const entryId = entry.target.id || '';
        const match = this.links.find((link) => {
          const linkTargetId = this.getLinkTargetId(link);
          if (!linkTargetId) return false;

          return (
            (entryId && (entryId === linkTargetId || entryId.includes(linkTargetId))) ||
            entry.target.matches(`[data-section="${linkTargetId}"]`)
          );
        });

        if (!match) return;

        this.setActiveLink(match);
        const targetId = this.getLinkTargetId(match);
        window.ActiveWinningCollectionHeroId = targetId;
        this.activateWinningCollectionForId(targetId);
      });
    }, options);

    elements.forEach((element) => this.observer.observe(element));

    if (this.debug) {
      console.log('SecondaryHeader: observer started', {
        observed: elements.length,
        rootMargin
      });
    }
  }

  initInitialActivation() {
    const stored = (() => {
      try {
        return sessionStorage.getItem('SecondaryHeaderActiveId') || '';
      } catch (error) {
        return '';
      }
    })();

    const hash = window.location.hash ? this.normalizeId(window.location.hash) : '';
    const serverActive = this.header?.getAttribute('data-active-collection') || '';

    if (hash) {
      this.activateWinningCollectionForId(hash);
      const targetLink = this.findLinkByTargetId(hash);
      if (targetLink) this.setActiveLink(targetLink);

      const targetEl = this.sections.get(hash) || this.findSectionElement(hash);
      if (targetEl) this.scrollToElement(targetEl);
      return;
    }

    if (stored) {
      const targetLink = this.findLinkByTargetId(stored);
      if (targetLink) {
        this.activateWinningCollectionForId(stored);
        this.setActiveLink(targetLink);
        return;
      }
    }

    if (serverActive) {
      const targetLink = this.findLinkByCollectionKey(serverActive);
      if (targetLink) {
        const targetId = this.getLinkTargetId(targetLink);
        if (targetId) this.activateWinningCollectionForId(targetId);
        this.setActiveLink(targetLink);
        return;
      }
    }

    const firstLink = this.links[0];
    if (firstLink) {
      const targetId = this.getLinkTargetId(firstLink);
      if (targetId) this.activateWinningCollectionForId(targetId);
      this.setActiveLink(firstLink);
    }
  }

  initHashSync() {
    window.addEventListener('hashchange', () => {
      const hash = window.location.hash ? this.normalizeId(window.location.hash) : '';
      if (!hash) return;

      const targetLink = this.findLinkByTargetId(hash);
      if (targetLink) {
        this.setActiveLink(targetLink);
        try {
          sessionStorage.setItem('SecondaryHeaderActiveId', hash);
        } catch (error) {
          // Ignore storage errors.
        }
      }
    });
  }

  activateWinningCollectionForId(sectionId) {
    const id = this.normalizeId(sectionId);
    const isDesktop = window.innerWidth > 750;
    const allCollections = document.querySelectorAll('.winning-team-collection');

    allCollections.forEach((collection) => {
      const assoc = (collection.dataset.associatedHero || '').trim();
      const isMatch =
        assoc === id ||
        assoc.replace(/[\s_-]+/g, '').toLowerCase() === id.replace(/[\s_-]+/g, '').toLowerCase();

      collection.setAttribute('data-autoplay-state', isMatch ? 'active' : 'inactive');

      if (isMatch) return;

      collection.querySelectorAll('.winning-team-video').forEach((video) => {
        try {
          video.pause();
          video.currentTime = 0;
          const slide = video.closest('.winning-team-slide');
          const thumbnail = slide?.querySelector('.video-thumbnail-wrapper');
          video.classList.remove('video-ready');
          if (thumbnail) thumbnail.classList.remove('video-playing');
        } catch (error) {
          // Ignore video reset errors.
        }
      });
    });

    let activeCollection = null;
    document.querySelectorAll('.winning-team-collection').forEach((collection) => {
      if (activeCollection) return;

      const assoc = (collection.dataset.associatedHero || '').trim();
      const isMatch =
        assoc === id ||
        assoc.replace(/[\s_-]+/g, '').toLowerCase() === id.replace(/[\s_-]+/g, '').toLowerCase();

      if (isMatch) activeCollection = collection;
    });

    if (!activeCollection) {
      window.DisableGlobalWinningAutoplay = false;
      return;
    }

    const associationEnabled = activeCollection.dataset.associationEnabled === 'true';
    const associationEnabledMobile = activeCollection.dataset.associationEnabledMobile === 'true';
    const cards = activeCollection.querySelectorAll('.winning-team-card');

    if ((isDesktop && associationEnabled) || (!isDesktop && associationEnabledMobile)) {
      window.DisableGlobalWinningAutoplay = true;
      cards.forEach((card) => {
        try {
          if (window.initVideoAutoplay) window.initVideoAutoplay(card);
        } catch (error) {
          // Ignore autoplay init errors.
        }
      });
      return;
    }

    window.DisableGlobalWinningAutoplay = false;
  }
}

function getTargetIdFromLink(link) {
  if (!link) return '';

  const dataSection = link.getAttribute('data-section');
  if (dataSection) return dataSection.trim().replace(/^#/, '');

  const href = link.getAttribute('href') || '';
  const hashIndex = href.indexOf('#');
  if (hashIndex === -1) return '';

  return href.slice(hashIndex + 1).trim().replace(/^#/, '');
}

function getDesktopLabelFromLink(link) {
  const desktopText = link?.querySelector('.secondary-header__text-desktop');
  if (desktopText) return desktopText.textContent.trim();
  return link?.textContent?.trim() || '';
}

function startSecondaryHeader() {
  new SecondaryHeader({ debug: false });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startSecondaryHeader);
} else {
  startSecondaryHeader();
}

const secondaryHeader = document.querySelector('.secondary-header');
const mainHeader = document.querySelector('#header-component');

function updateSecondaryHeaderTop() {
  if (!mainHeader || !secondaryHeader) return;

  const stickyMode = mainHeader.getAttribute('sticky');
  const stickyState = mainHeader.dataset.stickyState;

  if (stickyMode === 'hide-show-scroll') {
    if (stickyState === 'active') {
      secondaryHeader.style.top = `${mainHeader.offsetHeight}px`;
    } else {
      secondaryHeader.style.top = '0px';
    }
    return;
  }

  secondaryHeader.style.top = `${mainHeader.offsetHeight}px`;
}

function detectComparePageCollection() {
  if (!window.location.pathname.includes('compare')) return;

  const secondaryHeaderEl = document.querySelector('.secondary-header');
  if (!secondaryHeaderEl) return;

  const urlParams = new URLSearchParams(window.location.search);
  let detectedCollection = urlParams.get('collection') || '';

  if (!detectedCollection) {
    const productElements = document.querySelectorAll('[data-product-handle], .product-item, .compare-product');

    for (const element of productElements) {
      const productHandle =
        (element.dataset ? element.dataset.productHandle : null) ||
        element.querySelector('[data-product-handle]')?.getAttribute('data-product-handle') ||
        '';
      const productTitle = element.querySelector('.product-title, h3, h2')?.textContent || '';
      const searchText = `${productHandle} ${productTitle}`.toLowerCase();

      if (searchText.includes('student')) {
        detectedCollection = 'students';
        break;
      }

      if (searchText.includes('office')) {
        detectedCollection = 'office';
        break;
      }

      if (searchText.includes('pro')) {
        detectedCollection = 'pro';
        break;
      }

      if (searchText.includes('cloud')) {
        detectedCollection = 'cloud';
        break;
      }

      if (
        searchText.includes('libernovo') ||
        searchText.includes('omni') ||
        searchText.includes('maxis')
      ) {
        detectedCollection = 'libernovo';
      }
    }
  }

  if (!detectedCollection) return;

  secondaryHeaderEl.setAttribute('data-active-collection', detectedCollection);

  let productType = urlParams.get('product') || '';
  if (productType) productType = productType.split('-')[0];

  if (!productType) {
    const productTitleEl = document.querySelector('h1, .product-title, [data-product-title]');
    if (productTitleEl) {
      productType = (productTitleEl.textContent || '').split(' ')[0];
    }
  }

  const links = secondaryHeaderEl.querySelectorAll('.secondary-header__link');
  links.forEach((link) => {
    link.classList.remove('secondary-header__link--active');
    link.classList.remove('active');
    link.removeAttribute('aria-current');
  });

  const items = secondaryHeaderEl.querySelectorAll('.secondary-header__item');
  items.forEach((item) => item.classList.remove('secondary-header__item--mobile-only'));

  const activeLink = secondaryHeaderEl.querySelector(`[data-collection-key="${detectedCollection}"]`);
  if (!activeLink) return;

  activeLink.classList.add('secondary-header__link--active');
  activeLink.classList.add('active');
  activeLink.setAttribute('aria-current', 'true');

  const parentItem = activeLink.closest('.secondary-header__item');
  if (parentItem) {
    parentItem.classList.add('secondary-header__item--mobile-only');
  }

  const targetId = getTargetIdFromLink(activeLink);
  if (targetId) {
    try {
      sessionStorage.setItem('SecondaryHeaderActiveId', targetId);
    } catch (error) {
      // Ignore storage errors.
    }
  }

  const mobileTextEl = activeLink.querySelector('.secondary-header__text-mobile');
  if (mobileTextEl && productType) {
    const desktopLabel = getDesktopLabelFromLink(activeLink);
    const formattedProductType = productType.replace(/-/g, ' ').toUpperCase();
    mobileTextEl.textContent = `${desktopLabel} / ${formattedProductType} / השוואה`;
  }
}

updateSecondaryHeaderTop();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', detectComparePageCollection);
} else {
  detectComparePageCollection();
}

window.addEventListener('resize', updateSecondaryHeaderTop);
window.addEventListener('scroll', updateSecondaryHeaderTop);
